/**
=========================================================
* STAIL - useDebounce Hook
=========================================================
* Custom hook for debouncing values (e.g., search inputs)
* Updated: November 6, 2025 - Standardized to 400ms delay
=========================================================
*/

import { useState, useEffect } from 'react';

/**
 * Custom hook for debouncing values to reduce unnecessary operations.
 * Commonly used for search inputs to delay API calls until user stops typing.
 * 
 * @param {any} value - The value to debounce
 * @param {number} delay - Delay in milliseconds (default: 400ms for consistency)
 * @returns {any} The debounced value
 * 
 * Usage:
 * const [searchTerm, setSearchTerm] = useState("");
 * const debouncedSearch = useDebounce(searchTerm, 400);
 * 
 * useEffect(() => {
 *   // This only runs 400ms after user stops typing
 *   if (debouncedSearch) {
 *     performSearch(debouncedSearch);
 *   }
 * }, [debouncedSearch]);
 */
export function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  
  return debounced;
}

export default useDebounce;
